import type {
  DateFilterField,
  FilterField,
  FilterOption,
  SelectFilterField,
} from '@/components/common/AdvancedFilter';
import type { FiscalYear } from '@/features/dcrApprove/api/fiscalYear.api';
import type { ExamBoard } from '@/features/examBoards/types/examBoard.types';
import type { GeoUnit } from '@/features/geo/types/geo.types';
import type { InstituteType } from '@/features/instituteTypes/types/instituteType.types';
import type { OrganizationItem } from '@/features/organization/types/organization.types';
import type { ReportingAuthority } from '@/features/users/types/user.types';
import { getToday } from '@/utils/date';
import {
  ACTIVE_STATUS_FILTER_VALUE,
  FILTER_KEYS,
  OPTION_DETAIL_SEPARATOR,
  toFilterOptions,
} from '@/utils/filters';
import { getPersonOptionDetail } from '@/utils/personOption';

export const DATE_FROM_FILTER_KEY = 'dateFrom';
export const DATE_TO_FILTER_KEY = 'dateTo';
export const DATE_RANGE_FILTER_KEYS: readonly string[] = [DATE_FROM_FILTER_KEY, DATE_TO_FILTER_KEY];

const SELECT_DIVISION_FIRST = 'Select a division first';
const SELECT_DISTRICT_FIRST = 'Select a district first';

const DIVISION_FIRST_FIELD_STATE = {
  placeholder: SELECT_DIVISION_FIRST,
  disabled: true,
  disabledHint: SELECT_DIVISION_FIRST,
} as const;

export const DISTRICT_FIRST_FIELD_STATE = {
  placeholder: SELECT_DISTRICT_FIRST,
  disabled: true,
  disabledHint: SELECT_DISTRICT_FIRST,
} as const;

export function withRequiredFilterFields(
  fields: FilterField[],
  requiredKeys: readonly string[],
): FilterField[] {
  return fields.map((field) =>
    requiredKeys.includes(field.key) ? { ...field, isRequired: true } : field,
  );
}

export function getDateFilterField(label = 'Date', key = DATE_FROM_FILTER_KEY): DateFilterField {
  return { key, label, type: 'date' };
}

interface DateFromToFieldOptions {
  noFuture?: boolean;
  fromKey?: string;
  toKey?: string;
  labelPrefix?: string;
}

export function getDateFromToFilterFields({
  fromKey = DATE_FROM_FILTER_KEY,
  toKey = DATE_TO_FILTER_KEY,
  labelPrefix = 'Date',
  noFuture = false,
}: DateFromToFieldOptions = {}): DateFilterField[] {
  const cap = noFuture ? { maxDate: getToday() } : {};

  return [
    { key: fromKey, label: `${labelPrefix} From`, type: 'date', maxDateKey: toKey, ...cap },
    { key: toKey, label: `${labelPrefix} To`, type: 'date', minDateKey: fromKey, ...cap },
  ];
}

function toMemberFilterOptions(members: ReportingAuthority[] | undefined): FilterOption[] {
  return (members ?? []).map((member) => ({
    value: String(member.id),
    label: member.fullName,
    description: getPersonOptionDetail(member),
    searchText: [member.hrEmpId, member.username, member.mobile]
      .filter(Boolean)
      .join(OPTION_DETAIL_SEPARATOR),
  }));
}

interface MemberFilterFieldOptions {
  key: string;
  label: string;
  placeholder: string;
  members: ReportingAuthority[] | undefined;
}

export function getMemberFilterField({
  key,
  label,
  placeholder,
  members,
}: MemberFilterFieldOptions): SelectFilterField {
  return {
    key,
    label,
    type: 'select',
    placeholder,
    options: toMemberFilterOptions(members),
    searchable: true,
  };
}

export function getReportingMemberFilterField(
  members: ReportingAuthority[] | undefined,
  key = 'reportingMemberId',
): SelectFilterField {
  return getMemberFilterField({
    key,
    label: 'Reporting To',
    placeholder: 'All Reporting Members',
    members,
  });
}

const ACTIVE_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Active', value: ACTIVE_STATUS_FILTER_VALUE.active },
  { label: 'Inactive', value: ACTIVE_STATUS_FILTER_VALUE.inactive },
];

interface ActiveStatusFieldOptions {
  hasAllChoice?: boolean;
}

export function getActiveStatusFilterField(
  key = 'isActive',
  { hasAllChoice = false }: ActiveStatusFieldOptions = {},
): SelectFilterField {
  if (hasAllChoice) {
    return {
      key,
      label: 'Status',
      type: 'select',
      options: [{ label: 'All', value: ACTIVE_STATUS_FILTER_VALUE.all }, ...ACTIVE_STATUS_OPTIONS],
      isClearable: false,
    };
  }

  return {
    key,
    label: 'Status',
    type: 'select',
    placeholder: 'All',
    options: ACTIVE_STATUS_OPTIONS,
  };
}

export function getExamBoardFilterField(
  examBoards: ExamBoard[] | undefined,
  key = 'examBoardId',
): SelectFilterField {
  return {
    key,
    label: 'Exam Board',
    type: 'select',
    placeholder: 'All Exam Boards',
    options: toFilterOptions(examBoards),
    searchable: true,
  };
}

export function getInstituteTypeFilterField(
  instituteTypes: InstituteType[] | undefined,
  key = 'instituteTypeId',
): SelectFilterField {
  return {
    key,
    label: 'Institute Type',
    type: 'select',
    placeholder: 'All Institute Types',
    options: toFilterOptions(instituteTypes),
    searchable: true,
  };
}

export function getFiscalYearFilterField(
  fiscalYears: FiscalYear[] | undefined,
  key = 'fiscalYearId',
): SelectFilterField {
  const fiscalYearOptions = (fiscalYears ?? []).map((fiscalYear) => ({
    id: fiscalYear.id,
    name: fiscalYear.isCurrent ? `${fiscalYear.name} (current)` : fiscalYear.name,
  }));

  return {
    key,
    label: 'Fiscal Year',
    type: 'select',
    placeholder: 'All Fiscal Years',
    options: toFilterOptions(fiscalYearOptions),
  };
}

export function getZonalManagerFilterField(
  zonalManagers: ReportingAuthority[] | undefined,
  key = 'userId',
): SelectFilterField {
  return getMemberFilterField({
    key,
    label: 'ZM Name',
    placeholder: 'All ZMs',
    members: zonalManagers,
  });
}

interface CompanyDepartmentOptions {
  companies: OrganizationItem[] | undefined;
  departments: OrganizationItem[] | undefined;
}

export function getDepartmentFilterField(
  departments: OrganizationItem[] | undefined,
): SelectFilterField {
  return {
    key: FILTER_KEYS.departmentId,
    label: 'Department',
    type: 'select',
    placeholder: 'All Departments',
    options: toFilterOptions(departments),
    searchable: true,
  };
}

export function getCompanyDepartmentFilterFields({
  companies,
  departments,
}: CompanyDepartmentOptions): FilterField[] {
  return [
    {
      key: FILTER_KEYS.companyId,
      label: 'Company',
      type: 'select',
      placeholder: 'All Companies',
      options: toFilterOptions(companies),
      searchable: true,
    },
    getDepartmentFilterField(departments),
  ];
}

interface GeoOptions {
  divisions: GeoUnit[] | undefined;
  districts: GeoUnit[] | undefined;
  thanas: GeoUnit[] | undefined;
}

export function getGeoFilterFields(
  values: Record<string, string>,
  { divisions, districts, thanas }: GeoOptions,
): FilterField[] {
  const hasDivision = Boolean(values[FILTER_KEYS.divisionId]);
  const hasDistrict = Boolean(values[FILTER_KEYS.districtId]);

  return [
    {
      key: FILTER_KEYS.divisionId,
      label: 'Division',
      type: 'select',
      placeholder: 'All Divisions',
      options: toFilterOptions(divisions),
    },
    {
      key: FILTER_KEYS.districtId,
      label: 'District',
      type: 'select',
      options: toFilterOptions(districts),
      searchable: true,
      ...(hasDivision ? { placeholder: 'All Districts' } : DIVISION_FIRST_FIELD_STATE),
    },
    {
      key: FILTER_KEYS.thanaId,
      label: 'Thana',
      type: 'select',
      options: toFilterOptions(thanas),
      searchable: true,
      ...(hasDistrict ? { placeholder: 'All Thanas' } : DISTRICT_FIRST_FIELD_STATE),
    },
  ];
}
